import {
  RefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  DataGridPremiumProps,
  getGridStringOperators,
  GridCellParams,
  GridColumnGroupingModel,
  GridEventListener,
  GridFilterModel,
  GridPinnedColumnFields,
  GridRowGroupingModel,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { Table } from 'Common/MUITableV2'
import { Box, SxProps, Theme } from '@mui/material'
import { MUITableToolBar } from './MUITableToolbar'
import { BulkUpdateModal, ExtendedGridColDef } from './BulkUpdateModal'
import {
  discardChanges,
  saveChangesToServer,
} from 'Common/Components/MUITable.methods'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { searchTextOperator } from 'Common/MUITableV2/SearchText.operator'
import { useBlocker } from 'react-router-dom'
import { NavigationModal } from './NavigationModal'
import { toSentenceCase } from 'Common/Utils/utils'
import { DropZone } from './DropZone'
import { GridFrame } from 'Common/Components/GridFrame'
import { NoResultsOverlay, NoRowsOverlay } from 'Common/Components/GridOverlays'

export type BaseTableProps<T> = {
  data: T[]
  isLoading: boolean
  updateTableData: (changedRows: Record<number, T>) => void
  processRow?: (row: T, oldRow: T) => T
  /**
   * The rows with unsaved edits, as they are now and as they were before
   * editing (same order). Called after every edit, bulk update, undo, redo,
   * save and discard, so a page can preview the effect (line settings).
   */
  onPendingChange?: (pending: { current: T[]; before: T[] }) => void
  updateUnsavedRows?: (
    unsavedChangesRef: RefObject<{
      unsavedRows: Record<GridRowId, T>
      rowsBeforeChange: Record<GridRowId, T>
    }>
  ) => void
  uploadData?: (file: File) => void
  isUploading?: boolean
  isSaving: boolean
  checkBoxSelection?: boolean
  showTopToolBar?: boolean
  columns?: ExtendedGridColDef[]
  showTableToolBar?: boolean
  handleBulkUpdateButtonClick?: () => void
  downloadFileName?: string
  handleRowClick?: GridEventListener<'rowClick'>
  pinnedColumns?: string[]
  toolBarChildren?: ReactNode
  isCellEditable?: (params: GridCellParams) => boolean
  columnGroupingModel?: GridColumnGroupingModel
  rowGroupingModel?: GridRowGroupingModel
  groupingColDef?: Record<string, unknown>
  sx?: SxProps<Theme>
  customToolbar?: (props: {
    selectedRows: GridRowSelectionModel
    hasUnsavedRows: boolean
    isSaving: boolean
    data: T[]
    fileName: string
  }) => () => ReactNode
  // New props for CombinedView support
  onCellClick?: GridEventListener<'cellClick'>
  hideFooter?: boolean
  rowHeight?: number
  initialState?: DataGridPremiumProps['initialState']
  onMouseMove?: (event: React.MouseEvent<HTMLDivElement>) => void
  apiRefCallback?: (apiRef: RefObject<GridApiPremium>) => void
  getRowClassName?: DataGridPremiumProps['getRowClassName']
  // Master-Detail Panel support
  getDetailPanelContent?: (
    params: GridRowParams<GridValidRowModel>
  ) => ReactNode
  getDetailPanelHeight?: DataGridPremiumProps['getDetailPanelHeight']
  detailPanelExpandedRowIds?: GridRowId[]
  onDetailPanelExpandedRowIdsChange?: (newIds: GridRowId[]) => void
  // Aggregation support
  aggregationFunctions?: DataGridPremiumProps['aggregationFunctions']
  // getRowId support
  getRowId?: DataGridPremiumProps['getRowId']
  density?: 'compact' | 'standard' | 'comfortable'
  // New props for DropZone integration
  multiStageUpload?: {
    uploadFile: (file: File) => void
    isUploading: boolean
    columns: ExtendedGridColDef[]
    updateRowsStatus: Record<string, unknown> | undefined
    originalData: T[]
    predicate: string[] | undefined
    dataConfig: { required: string[]; optional: string[] }
  }
  tableHeight?: number
  onFilterModelChange?: (model: GridFilterModel) => GridFilterModel
  userDefinedFilterModel?: GridFilterModel
  customDownloadDataMethod?: (tableData: T[]) => T[]
  /** Why the pending edits can't be saved yet; disables Save and says why. */
  saveBlockedReason?: string | null
  getCellClassName?: DataGridPremiumProps['getCellClassName']
  onCellKeyDown?: DataGridPremiumProps['onCellKeyDown']
  /** Select a range of cells (and paste one value into all of them). */
  cellSelection?: boolean
  /** Rows nested under other rows (Total production: SKU groups under their batch). */
  treeData?: boolean
  getTreeDataPath?: DataGridPremiumProps['getTreeDataPath']
  /** How many levels start expanded; -1 (the default) is all of them. */
  defaultGroupingExpansionDepth?: number
}

export type UnsavedChangesContextType = {
  unsavedChangesRef: RefObject<{
    unsavedRows: Record<GridRowId, unknown>
    rowsBeforeChange: Record<GridRowId, unknown>
  }> | null
  hasUnsavedRows: boolean
  setHasUnsavedRows: (value: boolean) => void
  undoRedoPointer: RefObject<number>
  undoRedoStack: RefObject<GridRowId[]>
  apiRef: RefObject<GridApiPremium>
  selectedRows: GridRowSelectionModel
}

const checkBoxSelectionColumnName = '__check__'

export const BaseTable = <T extends GridValidRowModel>({
  data,
  isLoading,
  updateTableData,
  processRow,
  onPendingChange,
  uploadData,
  isUploading,
  isSaving,
  checkBoxSelection = false,
  //showTopToolBar = true,
  columns,
  multiStageUpload,
  // New props for CombinedView support
  onCellClick,
  hideFooter = false,
  rowHeight,
  initialState,
  onMouseMove,
  apiRefCallback,
  getRowClassName: customGetRowClassName,
  // Master-Detail Panel props
  getDetailPanelContent,
  getDetailPanelHeight,
  detailPanelExpandedRowIds,
  onDetailPanelExpandedRowIdsChange,
  // Aggregation props
  aggregationFunctions,
  getRowId,
  showTableToolBar = true,
  handleBulkUpdateButtonClick = () => {
    console.log('Bulk update button clicked')
  },
  downloadFileName = 'fileDownload',
  handleRowClick = undefined,
  pinnedColumns = [],
  //toolBarChildren,
  isCellEditable,
  columnGroupingModel = [],
  rowGroupingModel = [],
  groupingColDef = {},
  sx,
  customToolbar,
  density = 'compact',
  tableHeight = undefined,
  onFilterModelChange = undefined,
  userDefinedFilterModel = undefined,
  customDownloadDataMethod = undefined,
  saveBlockedReason = null,
  ...props
}: BaseTableProps<T> & { sx?: SxProps<Theme> }) => {
  const apiRef = useGridApiRef() as RefObject<GridApiPremium>
  // Call apiRefCallback if provided to allow external access to apiRef
  useEffect(() => {
    if (apiRefCallback) {
      apiRefCallback(apiRef)
    }
  }, [apiRefCallback])

  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  })

  const [pinnedColumnsToLeft, setPinnedColumnsToLeft] = useState<string[]>([
    checkBoxSelectionColumnName,
    ...pinnedColumns,
  ])

  // Convert hasUnsavedRows from ref to state
  const [hasUnsavedRows, setHasUnsavedRows] = useState<boolean>(false)
  const blocker = useBlocker(hasUnsavedRows)

  const handleCancel = () => {
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }

  const handleProceed = () => {
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }

  // Add density state for better handling of large datasets
  const [systemDensity, setSystemDensity] = useState<
    'compact' | 'standard' | 'comfortable'
  >(density)

  const unsavedChangesRef = useRef<{
    unsavedRows: Record<GridRowId, T>
    rowsBeforeChange: Record<GridRowId, T>
  }>({
    unsavedRows: {},
    rowsBeforeChange: {},
  })

  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false)

  // A ref, because the toolbar's save and discard handlers are memoised.
  const onPendingChangeRef = useRef(onPendingChange)
  onPendingChangeRef.current = onPendingChange

  const notifyPending = (latest?: Record<GridRowId, T>) => {
    const onPendingChange = onPendingChangeRef.current
    if (!onPendingChange) return
    const before = Object.values(unsavedChangesRef.current.rowsBeforeChange)
    const current = before.map((row) => {
      const id = (row as GridValidRowModel).id as GridRowId
      return (latest?.[id] ??
        (apiRef.current?.getRow(id) as T | null) ??
        unsavedChangesRef.current.unsavedRows[id]) as T
    })
    onPendingChange({ current, before })
  }

  // Helper function to calculate the new value based on operation
  const calculateValue = (
    value: unknown,
    currentValue: unknown,
    row: GridValidRowModel
  ): unknown => {
    // A column's own bulk action (BulkUpdateModal bulkActions): from the row.
    if (value && typeof value === 'object' && 'apply' in value) {
      return (value as { apply: (row: GridValidRowModel) => unknown }).apply(
        row
      )
    }
    // Check if this is an operation-based update
    if (
      value &&
      typeof value === 'object' &&
      'operation' in value &&
      'value' in value
    ) {
      const bulkValue = value as {
        operation: string
        value: string | number | null
      }
      const percentage = Number(bulkValue.value)

      if (Number.isNaN(percentage) || currentValue === null) {
        return currentValue // Keep original if percentage is invalid
      }

      const current = Number(currentValue)
      if (Number.isNaN(current)) {
        return currentValue // Keep original if current value is not a number
      }

      if (bulkValue.operation === 'increase_by_percentage') {
        return current + (current * percentage) / 100
      } else if (bulkValue.operation === 'decrease_by_percentage') {
        return current - (current * percentage) / 100
      }
    }

    // For regular value updates
    return value
  }

  // Handle bulk update values directly in BaseTable to avoid re-renders
  const handleBulkApply = (values: Record<string, unknown>) => {
    if (!Object.keys(values).length) return

    const updatedRows: GridValidRowModel[] = []

    // Handle different selection types
    if (selectedRows.type === 'include') {
      // Individual selection: iterate through selected IDs
      const selectedRowIds = selectedRows.ids || new Set()
      for (const rowId of selectedRowIds) {
        const oldRow = apiRef?.current?.getRow(rowId)
        if (!oldRow) continue

        // Calculate new values with operations
        const updatedValues: Record<string, unknown> = {}
        for (const field in values) {
          updatedValues[field] = calculateValue(
            values[field],
            oldRow[field],
            oldRow
          )
        }

        const newRow = { ...oldRow, ...updatedValues }
        updatedRows.push(processRowUpdate(newRow, oldRow, { rowId }))
      }
    } else if (selectedRows.type === 'exclude') {
      // Select All: iterate through all rows except excluded ones
      const excludedIds = selectedRows.ids || new Set()
      for (const row of data) {
        if (excludedIds.has(row.id)) continue // Skip excluded rows

        const oldRow = apiRef?.current?.getRow(row.id)
        if (!oldRow) continue

        // Calculate new values with operations
        const updatedValues: Record<string, unknown> = {}
        for (const field in values) {
          updatedValues[field] = calculateValue(
            values[field],
            oldRow[field],
            oldRow
          )
        }

        const newRow = { ...oldRow, ...updatedValues }
        updatedRows.push(processRowUpdate(newRow, oldRow, { rowId: row.id }))
      }
    }

    apiRef?.current?.updateRows(updatedRows)
    notifyPending()
    setShowBulkUpdateModal(false)
  }

  const onBulkUpdateButtonClickHandler = useCallback(() => {
    setShowBulkUpdateModal(true)
    handleBulkUpdateButtonClick()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const undoRedoPointer = useRef<number>(-1)
  // This is to keep track of the rows are changed in order.
  // We keep the id of the row, and that points to either unsavedRows or rowsBeforeChange
  const undoRedoStack = useRef<GridRowId[]>([])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'z') {
        if (undoRedoPointer.current === -1) {
          return
        }
        if (undoRedoPointer.current >= undoRedoStack.current.length) {
          undoRedoPointer.current = undoRedoStack.current.length - 1
        }

        const undoObj =
          unsavedChangesRef.current.rowsBeforeChange[
            undoRedoStack.current[undoRedoPointer.current]
          ]
        if (!undoObj) {
          return
        }
        apiRef.current?.updateRows([undoObj])
        undoRedoPointer.current--
        notifyPending()

        // Check if there are still unsaved rows after undo
        if (undoRedoPointer.current === -1) {
          setHasUnsavedRows(false)
        }
      } else if (event.ctrlKey && event.key === 'y') {
        // Redo operation
        if (undoRedoPointer.current === undoRedoStack.current.length) {
          return
        }
        if (undoRedoPointer.current === -1) {
          undoRedoPointer.current = 0
        }

        const redoObj =
          unsavedChangesRef.current.unsavedRows[
            undoRedoStack.current[undoRedoPointer.current]
          ]
        if (!redoObj) {
          return
        }
        apiRef.current?.updateRows([redoObj])
        undoRedoPointer.current++
        notifyPending()
        // If we redo any changes, set hasUnsavedRows to true
        setHasUnsavedRows(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [apiRef])

  const processRowUpdate: NonNullable<
    DataGridPremiumProps['processRowUpdate']
  > = (newRow, oldRow) => {
    try {
      // Update hasUnsavedRows state instead of ref
      setHasUnsavedRows(true)
      const rowId = newRow.id
      // Implement later: add a method here update newRow, if the user wants to do specific changes.
      // newRow = updateRow(newRow)
      if (processRow) {
        // Can be used to apply any updates to other columns
        newRow = processRow(newRow, oldRow)
      }
      unsavedChangesRef.current.unsavedRows[rowId] = newRow
      if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
        unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow
      }
      notifyPending({ [rowId]: newRow } as Record<GridRowId, T>)
      undoRedoPointer.current = undoRedoPointer.current + 1
      undoRedoStack.current[undoRedoPointer.current] = rowId
      // If undoRedoPonter is not at the end of the stack, then remove all the elements after that.
      if (undoRedoPointer.current < undoRedoStack.current.length - 1) {
        undoRedoStack.current = undoRedoStack.current.slice(
          0,
          undoRedoPointer.current + 1
        )
      }

      return newRow
    } catch (error) {
      console.error('Error processing row update:', error)
      return oldRow
    }
  }

  const handlePinnedColumnChange = (pinnedColumns: GridPinnedColumnFields) => {
    if (!pinnedColumns.left) {
      return
    }
    const index = pinnedColumns.left.indexOf(checkBoxSelectionColumnName)
    if (index > -1) {
      pinnedColumns.left.splice(index, 1)
    }
    pinnedColumns.left.unshift(checkBoxSelectionColumnName)
    setPinnedColumnsToLeft(pinnedColumns.left)
  }

  // Create a stable hash of the data for memoization to avoid expensive recalculations
  // while still updating when data content actually changes
  const dataHash = useMemo(() => {
    if (!data?.length || !columns?.length) return ''

    // Only hash the fields that are actually used in string columns
    const stringColumnFields = columns
      .filter((column) => column?.type === 'string' && column?.field)
      .map((column) => column.field)

    if (!stringColumnFields.length) return ''

    // Create a minimal hash of only the relevant field values
    const relevantData = data
      .map((item) => stringColumnFields.map((field) => item[field]).join('|'))
      .join('||')

    // Use a simple hash to avoid JSON.stringify overhead for large datasets
    let hash = 0
    for (let i = 0; i < relevantData.length; i++) {
      const char = relevantData.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32bit integer
    }
    return hash.toString()
  }, [data, columns])

  // Memoize unique values for each column separately to avoid recalculations
  const uniqueColumnValues = useMemo(() => {
    if (!columns || !data?.length) return {}

    // Create a map to store unique values for each string column
    const uniqueValues: Record<string, Set<unknown>> = {}

    // Find all string columns first
    const stringColumns = columns.filter((column) => column?.type === 'string')

    // Then compute unique values for each string column only once
    stringColumns.forEach((column) => {
      if (column?.field) {
        uniqueValues[column.field] = new Set(
          data
            .map((item) => item[column.field])
            .filter(
              (value) => value !== null && value !== undefined && value !== ''
            )
        )
      }
    })

    return uniqueValues
  }, [columns, dataHash]) // Now depends on actual data content via hash

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    userDefinedFilterModel ?? {
      items: [],
    }
  )

  // Then use the pre-computed unique values in compiledColumns
  const compiledColumns = useMemo(() => {
    if (!columns?.length) return []

    return columns.map((column) => {
      // Apply sentence case to headerName
      const sentenceCaseHeader = column.headerName
        ? toSentenceCase(column.headerName)
        : column.headerName

      if (column?.type === 'string' && column.field) {
        const uniqueSet = uniqueColumnValues[column.field]
        // Only add filter operators if we have unique values to show
        if (uniqueSet && uniqueSet.size > 0) {
          return {
            ...column,
            headerName: sentenceCaseHeader,
            filterOperators: [
              {
                ...searchTextOperator[0],
                InputComponentProps: {
                  data: [...uniqueSet],
                  label: sentenceCaseHeader,
                },
              },
              ...getGridStringOperators(),
            ],
          }
        }
      }
      return {
        ...column,
        headerName: sentenceCaseHeader,
      }
    })
  }, [columns, uniqueColumnValues, dataHash, filterModel])

  // The upload opens this grid's own input, not the first element on the page
  // with the shared id — that broke as soon as two grids shared a page.
  const fileInputRef = useRef<HTMLInputElement>(null)

  const toolBarComponents = useMemo(() => {
    // Use custom toolbar if provided, otherwise use default MUITableToolBar
    if (customToolbar) {
      return customToolbar({
        selectedRows,
        hasUnsavedRows,
        isSaving,
        data,
        fileName: downloadFileName,
      })
    }

    return () => (
      <MUITableToolBar
        hasUnsavedRows={hasUnsavedRows}
        isSaving={isSaving}
        saveChanges={() => {
          saveChangesToServer<T>(
            apiRef as RefObject<GridApiPremium> & { current: GridApiPremium },
            unsavedChangesRef,
            updateTableData,
            undoRedoStack,
            undoRedoPointer,
            setHasUnsavedRows,
            setSelectedRows
          )
          notifyPending()
        }}
        discardChanges={() => {
          discardChanges<T>(
            apiRef as RefObject<GridApiPremium> & { current: GridApiPremium },
            unsavedChangesRef,
            undoRedoStack,
            undoRedoPointer,
            setHasUnsavedRows
          )
          notifyPending()
        }}
        dataToDownload={data}
        fileName={downloadFileName}
        selectedRows={selectedRows}
        onBulkUpdateButtonClick={onBulkUpdateButtonClickHandler}
        uploadData={uploadData}
        onUploadClick={() => fileInputRef.current?.click()}
        isUploading={isUploading || false}
        isLoading={isLoading}
        apiRef={
          apiRef as RefObject<GridApiPremium> & { current: GridApiPremium }
        }
        customDownloadDataMethod={customDownloadDataMethod as any}
        saveBlockedReason={saveBlockedReason}
      />
    )
  }, [
    customToolbar,
    selectedRows,
    hasUnsavedRows,
    isSaving,
    apiRef,
    unsavedChangesRef,
    updateTableData,
    undoRedoStack,
    undoRedoPointer,
    setHasUnsavedRows,
    setSelectedRows,
    data,
    downloadFileName,
    onBulkUpdateButtonClickHandler,
    uploadData,
    isUploading,
    isLoading,
    customDownloadDataMethod,
    saveBlockedReason,
  ])

  const [fileForUpload, setFileForUpload] = useState<File | null>(null)

  // Hidden file input for upload functionality - placed outside the DataGrid
  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    if (files && files.length > 0 && uploadData) {
      //uploadData(files[0])
      setFileForUpload(files[0])
      // If we haven't defined multistage upload, Directly upload with uploadData
      if (!multiStageUpload) {
        uploadData(files[0])
      }
      event.target.value = ''
    }
  }

  // GridFrame bounds the grid's height to what the window has left, so the
  // grid scrolls inside itself in both directions with its scrollbars in view.
  return (
    <GridFrame className="table-container-mui" height={tableHeight}>
      {/* Hidden file input for upload - outside DataGrid but accessible via permanent ID */}
      <input
        id="base-table-file-upload"
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      {/* Render the BulkUpdateModal */}
      <NavigationModal
        open={blocker.state === 'blocked'}
        handleCancel={handleCancel}
        handleProceed={handleProceed}
      />
      <BulkUpdateModal
        open={showBulkUpdateModal}
        onClose={() => setShowBulkUpdateModal(false)}
        selectedRows={selectedRows}
        columns={columns || []}
        onApply={handleBulkApply}
      />
      {multiStageUpload && (
        <DropZone
          uploadFile={multiStageUpload.uploadFile}
          isUploading={multiStageUpload.isUploading}
          columns={multiStageUpload.columns}
          updateRowsStatus={multiStageUpload.updateRowsStatus}
          originalData={multiStageUpload.originalData}
          predicate={multiStageUpload.predicate}
          dataConfig={multiStageUpload.dataConfig}
          file={fileForUpload}
        />
      )}
      <Box
        onMouseMove={onMouseMove}
        sx={{ position: 'relative', flex: 1, minHeight: 0, minWidth: 0 }}
      >
        <Table
          slots={{
            toolbar: toolBarComponents,
            noRowsOverlay: NoRowsOverlay,
            noResultsOverlay: NoResultsOverlay,
          }}
          showToolbar={showTableToolBar}
          checkboxSelection={checkBoxSelection}
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newRowSelectionModel) =>
            setSelectedRows(newRowSelectionModel)
          }
          pinnedColumns={{
            left: pinnedColumnsToLeft,
          }}
          onPinnedColumnsChange={handlePinnedColumnChange}
          apiRef={apiRef}
          disableRowSelectionOnClick
          loading={isLoading || isSaving}
          rows={data ?? []}
          columns={compiledColumns ?? []}
          getRowId={getRowId}
          showCellVerticalBorder
          showColumnVerticalBorder
          pagination={!hideFooter}
          hideFooter={hideFooter}
          {...(rowHeight && { rowHeight })}
          {...(handleRowClick && { onRowClick: handleRowClick })}
          {...(onCellClick && { onCellClick })}
          initialState={
            initialState || {
              pagination: { paginationModel: { pageSize: 100 } },
            }
          }
          pageSizeOptions={[25, 50, 100]}
          // Disable virtualization to eliminate rendering glitches
          disableVirtualization={true}
          // Virtualization optimization - increase buffer to reduce glitches
          rowBufferPx={500}
          columnBufferPx={150}
          density={systemDensity}
          processRowUpdate={(
            newRow: GridValidRowModel,
            oldRow: GridValidRowModel
          ) => {
            return processRowUpdate(newRow, oldRow, { rowId: newRow.id })
          }}
          getRowClassName={(params) => {
            const classes = []

            // Add edited row class
            if (unsavedChangesRef.current.unsavedRows[params.id]) {
              classes.push('row--edited')
            }

            // Add alternating row class based on actual row index
            if (params.indexRelativeToCurrentPage !== undefined) {
              classes.push(
                params.indexRelativeToCurrentPage % 2 === 0
                  ? 'row--even'
                  : 'row--odd'
              )
            }

            // Add custom classes from prop if provided
            if (customGetRowClassName) {
              const customClass = customGetRowClassName(params)
              if (customClass) {
                classes.push(customClass)
              }
            }

            return classes.join(' ')
          }}
          {...(isCellEditable && { isCellEditable })}
          {...(columnGroupingModel &&
            columnGroupingModel.length > 0 && { columnGroupingModel })}
          {...(rowGroupingModel &&
            rowGroupingModel.length > 0 && { rowGroupingModel })}
          {...(Object.keys(groupingColDef || {}).length > 0 && {
            groupingColDef,
          })}
          defaultGroupingExpansionDepth={-1} // Can pass in if more specific cases
          // Master-Detail Panel props
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          detailPanelExpandedRowIds={
            detailPanelExpandedRowIds
              ? new Set(detailPanelExpandedRowIds)
              : undefined
          }
          onDetailPanelExpandedRowIdsChange={(newIds) => {
            if (onDetailPanelExpandedRowIdsChange) {
              onDetailPanelExpandedRowIdsChange(Array.from(newIds))
            }
          }}
          // Aggregation props - only pass if defined to avoid errors
          {...(aggregationFunctions && { aggregationFunctions })}
          sx={[{ height: '100%' }, ...(Array.isArray(sx) ? sx : [sx])]}
          editMode="cell"
          onDensityChange={(newDensity) => {
            setSystemDensity(newDensity)
          }}
          disablePivoting
          {...props}
          filterModel={filterModel}
          onFilterModelChange={(newFilterModel) => {
            let filterModel = newFilterModel
            if (onFilterModelChange) {
              filterModel = onFilterModelChange(newFilterModel)
            }
            setFilterModel(filterModel)
          }}
        />
      </Box>
    </GridFrame>
  )
}
